import React, { useState, useEffect, useRef } from "react";

import { bool, func } from "prop-types";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

import { useTranslation } from "@lib/i18n";

import ArrowDownIcon from "@components/shared/Icons/ArrowDown";
import Icon from "@components/shared/Icons/Icon";
import PlusIcon from "@components/shared/Icons/PlusIcon";
import TitlePreconfiguration from "./TitlePreconfiguration";
import { useActiveCarPreconfigurationSelector } from "@redux/reducers/deal";
import { useDetectDevice } from "@hooks/useDetectDevice";
import { usePreconfigurationsSelector } from "@redux/reducers/appConfig";
import { withTabNavigation } from "@hoc/withTabNavigation";

const DropdownButton = withTabNavigation("button", "button");

const CollapsableContainerPreconfigurations = ({ children }) => {
  const { isDesktop } = useDetectDevice();
  const { t } = useTranslation();
  const preconfigurations = usePreconfigurationsSelector();

  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);
  const ctaRef = useRef(null);

  const activePreconfiguration = useActiveCarPreconfigurationSelector();

  useEffect(() => {
    setIsOpen(false);
  }, [activePreconfiguration.id]);

  useEffect(() => {
    const onClickAway = event => {
      if (
        dropdownRef.current?.contains(event.target) ||
        event.target === dropdownRef.current ||
        ctaRef.current?.contains(event.target) ||
        event.target === ctaRef.current
      ) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("click", onClickAway, true);

    return () => {
      document.removeEventListener("click", onClickAway, true);
    };
  }, []);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const motionContainerOpenMaxHeight = isDesktop ? "auto" : "100%";

  const otherPreconfigurationsCount = preconfigurations.length - 1;

  return (
    <div
      className={twMerge(
        "flex justify-center z-20",
        isOpen ? "fixed top-0 left-0 w-full h-[100svh] z-50" : "relative",
        "lg:size-auto lg:relative lg:justify-start lg:min-w-88",
        "max-lg:bg-primaryDark/95 max-lg:backdrop-blur-lg",
        "md:max-lg:mt-4 md:max-lg:rounded-md"
      )}
    >
      <div className="w-full">
        <div className="flex justify-start items-center px-4 gap-8 min-h-28 lg:h-full">
          <TitlePreconfiguration />
          <DropdownButton
            aria-label={`${t("preconfiguration.numberOthers", {
              count: preconfigurations.length - 1,
            })} ${t("preconfiguration.models")}`}
            className="transition duration-150 hover:scale-105 active:scale-95"
            onClick={toggleOpen}
          >
            <span
              className="flex flex-col justify-center items-center w-24 h-16 text-white border-2 border-white rounded-md cursor-pointer relative lg:min-w-16 lg:w-24 lg:h-16 lg:px-2 lg:py-1"
              ref={ctaRef}
            >
              {!isOpen && otherPreconfigurationsCount && (
                <span
                  className={twMerge(
                    "block h-6 text-white text-xs/3 pointer-events-none",
                    "transition-all duration-150"
                  )}
                >
                  {t("preconfiguration.numberOthers", {
                    count: otherPreconfigurationsCount,
                  })}{" "}
                  {t("preconfiguration.models")}
                </span>
              )}
              <Icon
                className={twMerge(
                  isOpen ? "hidden" : "block",
                  isOpen && "rotate-180",
                  "size-6 lg:block"
                )}
                icon={<ArrowDownIcon />}
              />
              <Icon
                className={twMerge(
                  isOpen ? "block" : "hidden",
                  "size-5 text-white stroke-white rotate-45 lg:hidden"
                )}
                icon={<PlusIcon />}
              />
            </span>
          </DropdownButton>
        </div>
        <div
          className={twMerge(
            "w-full",
            isOpen && !isDesktop ? "h-[calc(100svh-7rem)]" : "h-0"
          )}
          ref={dropdownRef}
        >
          <motion.div
            animate={isOpen ? "open" : "closed"}
            className={twMerge(
              "w-full relative z-50 bg-primaryDark/95 backdrop-blur-lg lg:rounded-b-md",
              !isOpen
                ? "overflow-y-hidden"
                : "border-2 border-primaryOrange lg:border-l-0 lg:border-r-0 lg:border-b-0"
            )}
            exit="closed"
            initial="closed"
            isOpen={isOpen}
            key="accordion"
            variants={{
              open: {
                opacity: 1,
                height: motionContainerOpenMaxHeight,
                display: "block",
              },
              closed: {
                opacity: 0,
                height: 0,
                transitionEnd: { display: "none" },
                transition: { duration: 0.35 },
              },
            }}
          >
            {children({ isOpen })}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

CollapsableContainerPreconfigurations.propTypes = {
  children: func.isRequired,
  isOpen: bool,
  isDisabled: bool,
  onChange: func,
  defaultOpen: bool,
};

export default CollapsableContainerPreconfigurations;
